import * as React from 'react';
import {
  Table,
  TableHeader,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  makeStyles,
  tokens,
  Button,
  Spinner,
  Text,
} from '@fluentui/react-components';
import { Edit20Regular, Delete20Regular } from '@fluentui/react-icons';
import { Project } from '../../../types/project';

const useStyles = makeStyles({
  container: {
    width: '100%',
  },
  table: {
    // Tabela visível apenas em desktop
    '@media (max-width: 768px)': {
      display: 'none',
    },
  },
  mobileCards: {
    display: 'none',
    '@media (max-width: 768px)': {
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacingVerticalM,
    },
  },
  card: {
    padding: tokens.spacingHorizontalL,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: tokens.spacingVerticalS,
    gap: tokens.spacingHorizontalS,
  },
  cardContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  cardNome: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground1,
  },
  cardDescricao: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
  },
  cardStatus: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
  },
  cardActions: {
    display: 'flex',
    gap: tokens.spacingHorizontalXS,
    flexShrink: 0,
  },
  actions: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: tokens.spacingVerticalXXL,
  },
  error: {
    color: tokens.colorPaletteRedForeground1,
    textAlign: 'center',
    padding: tokens.spacingVerticalL,
  },
  actionsCell: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
  },
  // Estilos para controlar largura das colunas
  idCell: {
    width: '6%',
    minWidth: '50px',
  },
  descricaoCell: {
    width: '25%',
    minWidth: '150px',
  },
  tipoCell: {
    width: '12%',
    minWidth: '100px',
  },
  integracaoCell: {
    width: '18%',
    minWidth: '120px',
  },
  eventosCell: {
    width: '8%',
    minWidth: '60px',
  },
  criadoPorCell: {
    width: '15%',
    minWidth: '100px',
  },
  criadoEmCell: {
    width: '11%',
    minWidth: '90px',
  },
  actionsHeaderCell: {
    width: '10%',
    minWidth: '120px',
  },
});

interface ProjectGridProps {
  projects: Project[];
  onEdit: (project: Project) => void;
  onDelete: (id: number) => void;
  loading?: boolean;
  error?: string | null;
}

const ProjectGrid: React.FC<ProjectGridProps> = ({
  projects,
  onEdit,
  onDelete,
  loading,
  error,
}) => {
  const styles = useStyles();

  if (loading) {
    return (
      <div className={styles.loading}>
        <Spinner size="large" label="Carregando projetos..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.error}>
        <Text>Erro ao carregar projetos: {error}</Text>
      </div>
    );
  }

  const columns = [
    { key: 'id', label: 'ID' },
    { key: 'descricao', label: 'Descrição' },
    { key: 'tipoDescricao', label: 'Tipo' },
    { key: 'integracaoEmpresa', label: 'Integração' },
    { key: 'eventosCount', label: 'Eventos' },
    { key: 'criadoPorNome', label: 'Criado por' },
    { key: 'criadoEm', label: 'Data Criação' },
    { key: 'acoes', label: 'Ações' },
  ];

  // Função helper para mapear chave da coluna para classe CSS
  const getColumnClass = (key: string) => {
    switch (key) {
      case 'id': return styles.idCell;
      case 'descricao': return styles.descricaoCell;
      case 'tipoDescricao': return styles.tipoCell;
      case 'integracaoEmpresa': return styles.integracaoCell;
      case 'eventosCount': return styles.eventosCell;
      case 'criadoPorNome': return styles.criadoPorCell;
      case 'criadoEm': return styles.criadoEmCell;
      case 'acoes': return styles.actionsHeaderCell;
      default: return '';
    }
  };

  // Função helper para renderizar conteúdo da célula
  const renderCellContent = (project: Project, key: string) => {
    switch (key) {
      case 'id':
        return project.id;
      case 'descricao':
        return project.descricao.length > 50
          ? `${project.descricao.substring(0, 50)}...`
          : project.descricao;
      case 'tipoDescricao':
        return project.tipoDescricao;
      case 'integracaoEmpresa':
        return project.integracaoEmpresa;
      case 'eventosCount':
        return project.eventosProjetos?.length || 0;
      case 'criadoPorNome':
        return project.criadoPorNome;
      case 'criadoEm':
        return new Date(project.criadoEm).toLocaleDateString('pt-BR');
      case 'acoes':
        return (
          <div className={styles.actionsCell}>
            <Button
              appearance="subtle"
              icon={<Edit20Regular />}
              onClick={() => onEdit(project)}
              size="small"
            >
              Editar
            </Button>
            <Button
              appearance="subtle"
              icon={<Delete20Regular />}
              onClick={() => onDelete(project.id)}
              size="small"
            >
              Excluir
            </Button>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className={styles.container}>
      {/* Tabela para desktop */}
      <Table className={styles.table}>
        <TableHeader>
          <TableRow>
            {columns.map((column) => (
              <TableHeaderCell key={column.key} className={getColumnClass(column.key)}>
                {column.label}
              </TableHeaderCell>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {projects.map((project) => (
            <TableRow key={project.id}>
              {columns.map((column) => (
                <TableCell key={column.key} className={getColumnClass(column.key)}>
                  {renderCellContent(project, column.key)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Cards para mobile */}
      <div className={styles.mobileCards}>
        {projects.map((project) => (
          <div key={project.id} className={styles.card}>
            <div className={styles.cardHeader}>
              <div className={styles.cardContent}>
                <Text className={styles.cardNome}>{project.descricao}</Text>
                <Text className={styles.cardDescricao}>
                  Tipo: {project.tipoDescricao}
                </Text>
                <Text className={styles.cardStatus}>
                  Integração: {project.integracaoEmpresa}
                </Text>
                <Text className={styles.cardStatus}>
                  Eventos: {project.eventosProjetos?.length || 0}
                </Text>
                <Text className={styles.cardStatus}>
                  Criado por: {project.criadoPorNome}
                </Text>
                <Text className={styles.cardStatus}>
                  Criado em: {new Date(project.criadoEm).toLocaleDateString('pt-BR')}
                </Text>
              </div>
              <div className={styles.cardActions}>
                <Button
                  appearance="subtle"
                  icon={<Edit20Regular />}
                  onClick={() => onEdit(project)}
                  size="small"
                >
                  Editar
                </Button>
                <Button
                  appearance="subtle"
                  icon={<Delete20Regular />}
                  onClick={() => onDelete(project.id)}
                  size="small"
                >
                  Excluir
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ProjectGrid;